import { ReactElement, useState } from 'react';

import Button from '~/ui/components/common/Button';
import Modal from '~/ui/components/common/Modal';
import CrisisListItemDetails from '~/ui/pages/Network/components/Crisis/CrisisListItemDetails';
import formatClientAddress from '~/utils/dataFormat/formatClientAddress';

import { useApiRequest } from '~/hooks/api';
import { ICrisisCall, ICrisisCallsAllocatedClients } from '~/services/api/crisis/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useCrisisSignalR from '~/ui/hooks/useCrisisSignalR';

import styles from './SignalRCrisis.module.scss';

const SignalRCrisisAlert = (): ReactElement => {
  const [crisisCall, setCrisisCall] = useState<ICrisisCall | null>(null);
  const { setCrisisCalls } = useStoreActions(actions => actions.crisis);
  const { crisisCalls } = useStoreState(state => state.crisis);
  const user = useStoreState(state => state.user.current);
  const clinicId = user?.clinic?.id;

  const { onGetCrisisCalls } = useStoreActions(actions => actions.crisis);

  const { refetch: refetchCrisisCalls } = useApiRequest<{
    crisisCalls: ICrisisCall[];
    allocatedClients: ICrisisCallsAllocatedClients[];
  }>(() => onGetCrisisCalls({ clinicId: String(clinicId) }), {
    enabled: false,
  });

  const onCrisisCallReceived = (data: ICrisisCall) => {
    setCrisisCall(data);
    setCrisisCalls([...crisisCalls, data]);
  };

  useCrisisSignalR<ICrisisCall>({ action: 'CrisisCallReceived', callback: onCrisisCallReceived });

  useCrisisSignalR({ action: 'CrisisVisitStarted', callback: refetchCrisisCalls });
  useCrisisSignalR({ action: 'CrisisVisitCancelled', callback: refetchCrisisCalls });
  useCrisisSignalR({ action: 'CrisisVisitFinished', callback: refetchCrisisCalls });
  useCrisisSignalR({ action: 'CrisisAllocationDeleted', callback: refetchCrisisCalls });

  const resetCrisisCall = () => {
    setCrisisCall(null);
  };

  if (!crisisCall) {
    return null;
  }

  return (
    <Modal
      open={!!crisisCall}
      className={styles.modal}
      onClose={resetCrisisCall}
      maxWidth="sm"
      fullWidth
    >
      <>
        <div className={styles.modalTitle}>New Crisis Client</div>
        <div className={styles.modalWrapper}>
          <CrisisListItemDetails
            photo={crisisCall.client.photo}
            name={`${crisisCall.client.firstName} ${crisisCall.client.lastName}`}
            emrMrn={crisisCall.client.emrMrn}
            location={formatClientAddress(crisisCall.address)}
            timeCall={crisisCall.receivedOn}
            coordinates={{ latitude: crisisCall.address?.lat, longitude: crisisCall.address?.lng }}
          />
        </div>
        <div className={styles.actionButtons}>
          <Button color="primary" variant="contained" onClick={resetCrisisCall}>
            Close
          </Button>
        </div>
      </>
    </Modal>
  );
};

export default SignalRCrisisAlert;
